import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular-boost';
import { Observable } from 'rxjs';
import { catchError, filter, map, startWith, take } from 'rxjs/operators';
import { IFunnel, InboxType, ISchedule } from '../interfaces/graph-types';

type ErrorCallback = (err: string) => any;
type SuccessCallback = (data: any) => any;

@Injectable()
export class FunnelService {
  // ------------------------------------------------------
  constructor(private apollo: Apollo) {}

  // ------------------------------------------------------
  public queryGraphQL(query, variables): Observable<any> {
    return this.apollo.watchQuery<any>({
      variables: variables,
      query: query,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: false,
    }).valueChanges;
  }

  // ------------------------------------------------------
  public queryGraphQLNoCache(query, variables): Observable<any> {
    return this.apollo.watchQuery<any>({
      variables: variables,
      query: query,
      fetchPolicy: 'no-cache', // 'network-only' 'cache-and-network', // 'no-cache', //   -> https://www.apollographql.com/docs/react/api/react-apollo.html#graphql-config-options-fetchPolicy
      notifyOnNetworkStatusChange: false,
    }).valueChanges; //.pipe(take(1));
  }

  // ------------------------------------------------------
  public funnelSettings(funnelId: string): Observable<IFunnel> {
    return this.apollo
      .watchQuery<{ funnel: IFunnel }>({
        variables: { funnelId: funnelId },
        query: gql`
          query funnelSettings($funnelId: ID!) {
            funnel(funnelId: $funnelId) {
              id
              name
              description
              allowReenter
              addedToLibrary
              unsubscribeHeader
              cancelColleagues
              continueOnReply
              enableCaptureForm
              captureForm
              sharing
              archived
              legacyId
              timeTable {
                id
                name
              }
              attributes {
                id
                name
                value
              }
              owner {
                email
                name
              }
            }
          }
        `,
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
      })
      .valueChanges.pipe(
        map(({ data }) => data.funnel),
        startWith(null),
      );
  }

  // ------------------------------------------------------
  public stats(funnelId: string) {
    return this.apollo
      .watchQuery<{ funnel: IFunnel }>({
        variables: { funnelId },
        query: gql`
          query funnelStats($funnelId: ID!) {
            funnel(funnelId: $funnelId) {
              id
              applicationCount
              jobSpec
              jobSpecTitle
            }
          }
        `,
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
      })
      .valueChanges.pipe(map(res => res.data.funnel));
  }

  // ------------------------------------------------------
  public funnelStats(funnelId: string) {
    return this.apollo
      .watchQuery<{ funnel: IFunnel }>({
        variables: { funnelId: funnelId },
        query: gql`
          query funnelStats($funnelId: ID!) {
            funnel(funnelId: $funnelId) {
              id
              name
              funnel {
                all
                none
                pending
                engaged
                completed
              }
            }
          }
        `,
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
      })
      .valueChanges.pipe(map(res => res.data.funnel));
  }

  // ------------------------------------------------------
  public inboxes(
    funnelId: string,
    inboxType: InboxType = null,
  ): Observable<IFunnel> {
    if (!funnelId) {
      console.error('inboxes / funnelId null');
    }

    return this.apollo
      .watchQuery<{ funnel: IFunnel }>({
        variables: { funnelId },
        query: gql`
          query funnelInboxes($funnelId: ID!) {
            funnel(funnelId: $funnelId) {
              id
              allInboxes {
                id
                name
                accredited
                disabled
                email
                type
                sharing
                assigned
                sendQueueSize
                dailySendingInfo {
                  quota
                  sendCount
                }
              }
            }
          }
        `,
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
      })
      .valueChanges.pipe(take(1))
      .pipe(
        map(({ data }) => data.funnel),
        startWith(null),
      );
  }

  // ------------------------------------------------------
  public schedules(funnelId: string): Observable<ISchedule[]> {
    let variables = { funnelId };

    let query = gql`
      query schedules($funnelId: ID!) {
        funnel(funnelId: $funnelId) {
          id
          schedules {
            id
            triggerAt
            checkAt
            prospectCount
            bucket {
              id
              name
              owner {
                id
                name
              }
            }
          }
        }
      }
    `;

    return this.queryGraphQL(query, variables).pipe(
      filter(response => response.data),
      map(({ data }) => data.funnel.schedules),
      startWith(null),
    );
  }

  // ------------------------------------------------------
  public setLive(
    funnelId: string,
    live: boolean,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = { funnelId, live };
    this.apollo
      .mutate({
        mutation: gql`
          mutation setFunnelLive($input: SetFunnelLiveInput!) {
            setFunnelLive(input: $input) {
              live
              error
            }
          }
        `,
        variables: { input: input },
        update: (store, results) => {
          // { data: { setProspectTag } }
          // We don't want to update the cache if result is an error

          if (results['data']['setFunnelLive']['error']) {
            return;
          }

          // Read the data from our cache for this query.
          let query = gql`
            query updateFunnelLiveState($funnelId: ID!) {
              funnel(funnelId: $funnelId) {
                id
                live
              }
            }
          `;
          const cache = store.readQuery({
            query: query,
            variables: { funnelId },
          });
          // update our flag status
          let funnel = cache['funnel'];
          funnel.live = live;
          // Write our data back to the cache.
          store.writeQuery({ query: query, data: cache });
        },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].setFunnelLive;
        if (data.error) {
          if (errorfunction) {
            errorfunction(data.error);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        } else {
          console.log('Success');
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public setArchived(
    funnelId: string,
    archived: boolean,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = { funnelId, archived };
    this.apollo
      .mutate({
        mutation: gql`
          mutation setFunnelArchived($input: SetFunnelArchivedInput!) {
            setFunnelArchived(input: $input) {
              archived
              error
            }
          }
        `,
        variables: { input: input },
        update: (store, results) => {
          // { data: { setProspectTag } }
          // We don't want to update the cache if result is an error

          if (results['data']['setFunnelArchived']['error']) {
            return;
          }

          // Read the data from our cache for this query.
          let query = gql`
            query updateFunnelArchivedState($funnelId: ID!) {
              funnel(funnelId: $funnelId) {
                id
                archived
              }
            }
          `;
          const cache = store.readQuery({
            query: query,
            variables: { funnelId },
          });
          // update our flag status
          let funnel = cache['funnel'];
          funnel.archived = archived;
          if (archived) {
            funnel.live = false;
          }
          // Write our data back to the cache.
          store.writeQuery({ query: query, data: cache });
        },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        console.log('Success');
        console.log(result);
        if (successfunction) {
          successfunction(result['data'].setFunnelArchived);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public sendTestEmails(
    funnelId: string,
    prospectId: string,
    inboxId: string,
    variationIds: string[],
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      funnelId,
      prospectId,
      inboxId,
      variationIds,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation sendTestEmails($input: SendTestEmailsInput!) {
            sendTestEmails(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].sendTestEmails;
        if (data.error) {
          if (errorfunction) {
            errorfunction(data.error);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        } else {
          console.log('Success');
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public setFunnelInbox(
    funnelId: string,
    inboxId: string,
    assigned: boolean,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      funnelId: funnelId,
      inboxId: inboxId,
      assigned: assigned,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation setFunnelInbox($input: SetFunnelInboxInput!) {
            setFunnelInbox(input: $input) {
              error
              assigned
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].setFunnelInbox;
        if (data.error) {
          if (errorfunction) {
            errorfunction(data.error);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        } else {
          console.log('Success');
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public recomputeFunnelStats(
    funnelId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      funnelId,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation recomputeFunnelStats($input: RecomputeFunnelStatsInput!) {
            recomputeFunnelStats(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].recomputeFunnelStats;
        if (data.error) {
          if (errorfunction) {
            errorfunction(data.error);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        } else {
          console.log('Success');
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public clone(
    funnelId: string,
    name: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      funnelId,
      name,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation cloneFunnel($input: CloneFunnelInput!) {
            cloneFunnel(input: $input) {
              funnelId
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].cloneFunnel;
        if (data.error) {
          if (errorfunction) {
            errorfunction(data.error);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        } else {
          console.log('Success');
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public bulkExportApplications(
    accountId: string,
    funnelId: string,
    searchFilter: any,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      funnelId,
      accountId,
      searchFilter: JSON.stringify(searchFilter),
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation bulkExportApplications(
            $input: BulkExportApplicationsInput!
          ) {
            bulkExportApplications(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        const data = result['data'].bulkExportApplications;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public exportApplications(
    accountId: string,
    funnelId: string,
    applicationIds: string[],
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      funnelId,
      applicationIds,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation exportApplications($input: ExportApplicationsInput!) {
            exportApplications(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].exportApplications;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
        } else {
          if (successfunction) {
            successfunction(data);
          }
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public setSettings(
    funnelId: string,
    settingName: string,
    value: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      funnelId: funnelId,
      settingName: settingName,
      value: value,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation setFunnelSetting($input: SetFunnelSettingInput!) {
            setFunnelSetting(input: $input) {
              status
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].setFunnelSetting;
        if (data.error) {
          if (errorfunction) {
            errorfunction(data.error);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        } else {
          console.log('Success');
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public moveStepUp(
    funnelId: string,
    stepId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      funnelId,
      stepId,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation moveStepUp($input: MoveStepUpInput!) {
            moveStepUp(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].moveStepUp;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public moveStepDown(
    funnelId: string,
    stepId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      funnelId,
      stepId,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation moveStepDown($input: MoveStepDownInput!) {
            moveStepDown(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].moveStepDown;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public pauseVariation(
    funnelId: string,
    stepVariationId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      funnelId,
      stepVariationId,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation pauseVariation($input: PauseVariationInput!) {
            pauseVariation(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].pauseVariation;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public unPauseVariation(
    funnelId: string,
    stepVariationId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      funnelId,
      stepVariationId,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation unPauseVariation($input: UnPauseVariationInput!) {
            unPauseVariation(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].unPauseVariation;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public createFunnelAttribute(
    funnelId: string,
    name: string,
    value: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      funnelId: funnelId,
      name: name,
      value: value,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation createFunnelAttribute($input: CreateFunnelAttributeInput!) {
            createFunnelAttribute(input: $input) {
              error
              funnelAttributeId
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].createFunnelAttribute;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public createSchedules(
    funnelId: string,
    bucketId: string,
    weekDays: boolean[],
    hourSelected: number,
    minuteSelected: number,
    prospectCount: number,
    timeZone: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      funnelId,
      bucketId,
      weekDays,
      hourSelected,
      minuteSelected,
      prospectCount,
      timeZone,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation createSchedules($input: CreateSchedulesInput!) {
            createSchedules(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].createSchedules;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public setAttributeValue(
    funnelId: string,
    funnelAttributeId: string,
    value: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      funnelId: funnelId,
      funnelAttributeId: funnelAttributeId,
      value: value,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation setFunnelAttributeValue(
            $input: SetFunnelAttributeValueInput!
          ) {
            setFunnelAttributeValue(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          } else {
            console.log('Error: ');
            console.log(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        if (successfunction) {
          successfunction(result['data'].setFunnelAttributeValue);
        } else {
          console.log('Success');
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public deleteSchedules(
    funnelId: string,
    scheduleIds: string[],
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      funnelId,
      scheduleIds,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation deleteSchedules($input: DeleteSchedulesInput!) {
            deleteSchedules(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          } else {
            console.log('Error: ');
            console.log(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        if (successfunction) {
          successfunction(result['data'].deleteSchedules);
        } else {
          console.log('Success');
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public deleteStepVariation(
    stepVariationId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      stepVariationId: stepVariationId,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation deleteStepVariation($input: DeleteStepVariationInput!) {
            deleteStepVariation(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          } else {
            console.log('Error: ');
            console.log(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        if (successfunction) {
          successfunction(result['data'].deleteStepVariation);
        } else {
          console.log('Success');
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public createOrUpdateWaitStep(
    baseVariationId: string,
    funnelId: string,
    stepVariationId: string,
    unit: string,
    value: number,
    business: boolean,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {}

  // ------------------------------------------------------
  public createOrUpdateInfoStep(
    baseVariationId: string,
    funnelId: string,
    stepVariationId: string,
    statement: string,
    descriptionHtml: string,
    descriptionExtract: string,
    acceptBox: boolean,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    if (!funnelId) {
      console.log(
        '!!! ERROR: createOrUpdateInfoStep called with funnelId is 0 !!!',
      );
    }
    const input = {
      baseVariationId,
      funnelId,
      stepVariationId,
      statement,
      descriptionHtml,
      descriptionExtract,
      acceptBox,
    };
    return this.apollo
      .mutate({
        mutation: gql`
          mutation createOrUpdateInfoStep(
            $input: CreateOrUpdateInfoStepInput!
          ) {
            createOrUpdateInfoStep(input: $input) {
              stepVariationId
              error
            }
          }
        `,
        variables: { input: input },
      })
      .subscribe(result => {
        let data = result['data']['createOrUpdateInfoStep'];
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
  }

  // ------------------------------------------------------
  public saveJobSpec(
    funnelId: string,
    jobSpecTitle: string,
    jobSpec: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    if (!funnelId) {
      console.log('!!! ERROR: saveJobSpec called with funnelId is 0 !!!');
    }
    const input = {
      funnelId,
      jobSpec,
      jobSpecTitle,
    };
    return this.apollo
      .mutate({
        mutation: gql`
          mutation saveJobSpec($input: SaveJobSpecInput!) {
            saveJobSpec(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .subscribe(result => {
        let data = result['data']['saveJobSpec'];
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
  }

  // ------------------------------------------------------
  public createOrUpdateTaskStep(
    baseVariationId: string,
    funnelId: string,
    stepVariationId: string,
    name: string,
    bodyHtml: string,
    bodyExtract: string,
    waitForCompletion: boolean,
    assignee: number,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    if (!funnelId) {
      console.log(
        '!!! ERROR: createOrUpdateTaskStep called with funnelId is 0 !!!',
      );
    }
    const input = {
      baseVariationId,
      funnelId,
      stepVariationId,
      name,
      bodyHtml,
      bodyExtract,
      waitForCompletion,
      assignee,
    };
    return this.apollo
      .mutate({
        mutation: gql`
          mutation createOrUpdateTaskStep(
            $input: CreateOrUpdateTaskStepInput!
          ) {
            createOrUpdateTaskStep(input: $input) {
              stepVariationId
              error
            }
          }
        `,
        variables: { input: input },
      })
      .subscribe(result => {
        let data = result['data']['createOrUpdateTaskStep'];
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
  }

  // ------------------------------------------------------
  public createOrUpdateEmailStep(
    baseVariationId: string,
    funnelId: string,
    stepVariationId: string,
    subject: string,
    bodyHtml: string,
    bodyExtract: string,
    continueThread: boolean,
    openTracking: boolean,
    clickTracking: boolean,
    asDraft: boolean,
    cced: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    if (!funnelId) {
      console.log(
        '!!! ERROR: createOrUpdateEmailStep called with funnelId is 0 !!!',
      );
    }
    const input = {
      baseVariationId,
      funnelId,
      stepVariationId,
      subject,
      bodyHtml,
      bodyExtract,
      continueThread,
      openTracking,
      clickTracking,
      asDraft,
      cced,
    };
    return this.apollo
      .mutate({
        mutation: gql`
          mutation createOrUpdateEmailStep(
            $input: CreateOrUpdateEmailStepInput!
          ) {
            createOrUpdateEmailStep(input: $input) {
              stepVariationId
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          } else {
            console.log('Error: ');
            console.log(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].createOrUpdateEmailStep;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
  }

  // ------------------------------------------------------
  public createOrUpdateWebhookStep(
    baseVariationId: string,
    funnelId: string,
    stepVariationId: string,
    targetUrl: string,
    request: string,
    params: string,
    cookies: string,
    payload: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    if (!funnelId) {
      console.log(
        '!!! ERROR: createOrUpdateWebhookStep called with funnelId is 0 !!!',
      );
    }
    const input = {
      baseVariationId,
      funnelId,
      stepVariationId,
      targetUrl,
      request,
      params,
      cookies,
      payload,
    };
    return this.apollo
      .mutate({
        mutation: gql`
          mutation createOrUpdateWebhookStep(
            $input: CreateOrUpdateWebhookStepInput!
          ) {
            createOrUpdateWebhookStep(input: $input) {
              stepVariationId
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          } else {
            console.log('Error: ');
            console.log(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].createOrUpdateWebhookStep;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
  }

  // ------------------------------------------------------
  public createOrUpdateInputStep(
    baseVariationId: string,
    funnelId: string,
    stepVariationId: string,
    statement: string,
    descriptionHtml: string,
    descriptionExtract: string,
    required: boolean,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    if (!funnelId) {
      console.log(
        '!!! ERROR: createOrUpdateInputStep called with funnelId is 0 !!!',
      );
    }
    const input = {
      baseVariationId,
      funnelId,
      stepVariationId,
      statement,
      descriptionHtml,
      descriptionExtract,
      required,
    };
    return this.apollo
      .mutate({
        mutation: gql`
          mutation createOrUpdateInputStep(
            $input: CreateOrUpdateInputStepInput!
          ) {
            createOrUpdateInputStep(input: $input) {
              stepVariationId
              error
            }
          }
        `,
        variables: { input: input },
      })
      .subscribe(result => {
        let data = result['data']['createOrUpdateInputStep'];
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
  }

  // ------------------------------------------------------
  public createOrUpdateMultiStep(
    baseVariationId: string,
    funnelId: string,
    stepVariationId: string,
    statement: string,
    descriptionHtml: string,
    descriptionExtract: string,
    choices: Array<{ value: string; score: number }>,
    minSelection: number,
    maxSelection: number,
    randomized: boolean,
    required: boolean,
    explanation: boolean,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    if (!funnelId) {
      console.log(
        '!!! ERROR: createOrUpdateMultiStep called with funnelId is 0 !!!',
      );
    }
    const input = {
      baseVariationId,
      funnelId,
      stepVariationId,
      statement,
      descriptionHtml,
      descriptionExtract,
      choices,
      minSelection,
      maxSelection,
      randomized,
      required,
      explanation,
    };
    return this.apollo
      .mutate({
        mutation: gql`
          mutation createOrUpdateMultiStep(
            $input: CreateOrUpdateMultiStepInput!
          ) {
            createOrUpdateMultiStep(input: $input) {
              stepVariationId
              error
            }
          }
        `,
        variables: { input: input },
      })
      .subscribe(result => {
        let data = result['data']['createOrUpdateMultiStep'];
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
  }

  // ------------------------------------------------------
  public createOrUpdatCheckpointStep(
    baseVariationId: string,
    funnelId: string,
    stepVariationId: string,
    statement: string,
    descriptionHtml: string,
    descriptionExtract: string,
    minScore: number,
    manualReview: boolean,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    if (!funnelId) {
      console.log(
        '!!! ERROR: createOrUpdateCheckpointStep called with funnelId is 0 !!!',
      );
    }
    const input = {
      baseVariationId,
      funnelId,
      stepVariationId,
      statement,
      descriptionHtml,
      descriptionExtract,
      minScore,
      manualReview,
    };
    return this.apollo
      .mutate({
        mutation: gql`
          mutation createOrUpdateCheckpointStep(
            $input: CreateOrUpdateCheckpointStepInput!
          ) {
            createOrUpdateCheckpointStep(input: $input) {
              stepVariationId
              error
            }
          }
        `,
        variables: { input: input },
      })
      .subscribe(result => {
        let data = result['data']['createOrUpdateCheckpointStep'];
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
  }

  // ------------------------------------------------------
  public createOrUpdateSmsStep(
    baseVariationId: string,
    funnelId: string,
    stepVariationId: string,
    textMessage: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    if (!funnelId) {
      console.log(
        '!!! ERROR: createOrUpdateSmsStep called with funnelId is 0 !!!',
      );
    }
    const input = {
      baseVariationId,
      funnelId,
      stepVariationId,
      textMessage,
    };
    return this.apollo
      .mutate({
        mutation: gql`
          mutation createOrUpdateSmsStep($input: CreateOrUpdateSmsStepInput!) {
            createOrUpdateSmsStep(input: $input) {
              stepVariationId
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          } else {
            console.log('Error: ');
            console.log(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data']['createOrUpdateSmsStep'];
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
  }

  // ------------------------------------------------------
  public createOrUpdateCheckpointStep(
    baseVariationId: string,
    funnelId: string,
    stepVariationId: string,
    description: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    if (!funnelId) {
      console.log(
        '!!! ERROR: createOrUpdateCheckpointStep called with funnelId is 0 !!!',
      );
    }
    const input = {
      baseVariationId,
      funnelId,
      stepVariationId,
      description,
    };
    return this.apollo
      .mutate({
        mutation: gql`
          mutation createOrUpdateCheckpointStep(
            $input: CreateOrUpdateCheckpointStepInput!
          ) {
            createOrUpdateCheckpointStep(input: $input) {
              stepVariationId
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          } else {
            console.log('Error: ');
            console.log(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].createOrUpdateCheckpointStep;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
  }

  // ------------------------------------------------------
  public createOrUpdateCallStep(
    baseVariationId: string,
    funnelId: string,
    stepVariationId: string,
    bodyHtml: string,
    bodyExtract: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    if (!funnelId) {
      console.log(
        '!!! ERROR: createOrUpdateCallStep called with funnelId is 0 !!!',
      );
    }
    const input = {
      baseVariationId,
      funnelId,
      stepVariationId,
      bodyHtml,
      bodyExtract,
    };
    return this.apollo
      .mutate({
        mutation: gql`
          mutation createOrUpdateCallStep(
            $input: CreateOrUpdateCallStepInput!
          ) {
            createOrUpdateCallStep(input: $input) {
              stepVariationId
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          } else {
            console.log('Error: ');
            console.log(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].createOrUpdateCallStep;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
  }

  // ------------------------------------------------------
  public addOrUpdateConditionTags(
    funnelStepVariationId: string,
    presentTagIds: string[],
    absentTagIds: string[],
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      funnelStepVariationId,
      presentTagIds,
      absentTagIds,
    };
    return this.apollo
      .mutate({
        mutation: gql`
          mutation addOrUpdateConditionTags(
            $input: AddOrUpdateConditionTagsInput!
          ) {
            addOrUpdateConditionTags(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          } else {
            console.log('Error: ');
            console.log(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].addOrUpdateConditionTags;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
  }

  // ------------------------------------------------------
  public addOrUpdateConditionClicks(
    funnelStepVariationId: string,
    conditionClicks: any[],
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      funnelStepVariationId,
      conditionClicks,
    };
    return this.apollo
      .mutate({
        mutation: gql`
          mutation addOrUpdateConditionClicks(
            $input: AddOrUpdateConditionClicksInput!
          ) {
            addOrUpdateConditionClicks(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          } else {
            console.log('Error: ');
            console.log(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].addOrUpdateConditionClicks;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
  }

  // ------------------------------------------------------
  public addOrUpdateConditionOpen(
    funnelStepVariationId: string,
    minOpens: number,
    neverOpened: boolean,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      funnelStepVariationId,
      minOpens,
      neverOpened,
    };
    return this.apollo
      .mutate({
        mutation: gql`
          mutation addOrUpdateConditionOpen(
            $input: AddOrUpdateConditionOpenInput!
          ) {
            addOrUpdateConditionOpen(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          } else {
            console.log('Error: ');
            console.log(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].addOrUpdateConditionOpen;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
  }

  // ------------------------------------------------------
  public deleteConditionTags(
    conditionTagIds: string[],
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      conditionTagIds,
    };
    return this.apollo
      .mutate({
        mutation: gql`
          mutation deleteConditionTags($input: DeleteConditionTagsInput!) {
            deleteConditionTags(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          } else {
            console.log('Error: ');
            console.log(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].deleteConditionTags;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
  }

  // ------------------------------------------------------
  public copyFunnelFromLibrary(
    funnelId: string,
    funnelLinkId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ): null {
    const input = {
      funnelId,
      funnelLinkId,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation copyFunnelFromLibrary($input: CopyFunnelFromLibraryInput!) {
            copyFunnelFromLibrary(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('ERROR');
          errorfunction(err);
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].copyFunnelFromLibrary;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
        } else {
          if (successfunction) {
            successfunction(data);
          }
        }
        return null;
      });
    return null;
  }
}
